import { t } from "@hakili/i18n";
import { EcranAVenir } from "@hakili/ui";
import type { ReactNode } from "react";

import type { EcranClient } from "../shell/routes.js";

/**
 * `P0-UIK-016` / round 203: **every client screen present and reachable.**
 *
 * The founder's frontend-first turn: *"EVERY screen the product will have is
 * present and reachable. A function not yet built renders as a VISIBLE,
 * clearly-marked, inert affordance bound to its real screen location."*
 *
 * ## What round 203 found, and why this file is small
 *
 * `shell/routes.ts` has declared all eleven screens since `P0-UIK-016`, and
 * `ClientShell` has called `resolveRoute` on them all along , **and `App.tsx`
 * passed it the literal string `"/dossier"`.** So the shell resolved a route
 * correctly, every time, to the same screen for ever. The map was written and
 * never read.
 *
 * That is why the cure is a registry rather than a router: nothing needed
 * building except the step from a resolved screen id to a component, and one
 * honest component per screen that has none yet.
 *
 * ## The registry is TOTAL, and the type is what makes it so
 *
 * `Record<EcranClient, ...>` , not a partial map with a fallback. A screen
 * added to `ECRANS_CLIENT` without a renderer is a TYPE ERROR here, which is
 * the same discipline `TOUS_LES_ECRANS` already carries against the whitelist:
 * *"a screen added to the table without being added to the whitelist would be a
 * 404 for a screen the app itself renders"*. A fallback would have re-opened
 * exactly that hole one layer down.
 */

export interface EcranProps {
  /** The route's captures, `:id` and `:token` among them. */
  readonly params: Readonly<Record<string, string>>;
  /** The one screen round 203 does not stub: the dossier, already built. */
  readonly dossier: ReactNode;
}

/** `"<app>:<screen>:<action>"`, assembled once so every stub spells it the same. */
const contexte = (ecran: EcranClient, action: string): string => `client:${ecran}:${action}`;

export const ECRANS: Readonly<Record<EcranClient, (props: EcranProps) => React.JSX.Element>> = {
  accueil: () => (
    <EcranAVenir
      titre={t("client.accueil.titre")}
      resume={t("client.accueil.resume")}
      contexte={contexte("accueil", "ouvrir")}
    />
  ),
  recherche: () => (
    <EcranAVenir
      titre={t("client.recherche.titre")}
      resume={t("client.recherche.resume")}
      contexte={contexte("recherche", "chercher")}
    />
  ),
  vehicule: () => (
    <EcranAVenir
      titre={t("client.vehicule.titre")}
      resume={t("client.vehicule.resume")}
      contexte={contexte("vehicule", "simuler")}
    />
  ),
  /**
   * `AVAILABILITY-QUEUE`, `M2`, point 3: BUILT, and rendered by `App` where
   * the client's own front queue has already been read , the shape
   * `vehicule` and `paiement` use. This entry is the registry's completeness.
   */
  queueFront: () => (
    <EcranAVenir
      titre={t("client.queueFront.titre")}
      resume={t("client.queueFront.resume")}
      contexte={contexte("queueFront", "choisir")}
    />
  ),
  eligibilite: () => (
    <EcranAVenir
      titre={t("client.eligibilite.titre")}
      resume={t("client.eligibilite.resume")}
      contexte={contexte("eligibilite", "calculer")}
    />
  ),
  // BUILT. The only screen of the eleven with a real component behind it, and
  // it is handed in rather than imported so this registry stays a map and does
  // not become a second place that knows how to load a dossier.
  dossier: ({ dossier }) => <>{dossier}</>,
  /**
   * `round CW`, `CW.1`: the drawing's fourth tab exists, and says so honestly.
   *
   * `CW.5` builds it against `GET /me/dossiers/{id}`. Until then it is an
   * `EcranAVenir` like its siblings , `R81` as amended: a screen ahead of its
   * doors says what it is waiting for, and a tab that led nowhere at all would
   * be the dead affordance the same rule abolished.
   */
  suivi: () => (
    <EcranAVenir
      titre={t("client.suivi.titre")}
      resume={t("client.suivi.resume")}
      contexte={contexte("suivi", "suivre")}
    />
  ),
  /**
   * `round C3-A`, `C3-A.2`: BUILT, and rendered by `App` where its three doors
   * have already been read , the shape `suivi` and `dossier` use. The registry
   * is typed `Record<EcranClient, ...>` so a screen added to the vocabulary and
   * not to this map does not compile, which is how this entry came to exist.
   */
  paiement: ({ dossier }) => <>{dossier}</>,
  pieces: () => (
    <EcranAVenir
      titre={t("client.pieces.titre")}
      resume={t("client.pieces.resume")}
      contexte={contexte("pieces", "envoyer")}
    />
  ),
  offres: () => (
    <EcranAVenir
      titre={t("client.offres.titre")}
      resume={t("client.offres.resume")}
      contexte={contexte("offres", "choisir")}
    />
  ),
  assurance: () => (
    <EcranAVenir
      titre={t("client.assurance.titre")}
      resume={t("client.assurance.resume")}
      contexte={contexte("assurance", "souscrire")}
    />
  ),
  retrait: () => (
    <EcranAVenir
      titre={t("client.retrait.titre")}
      resume={t("client.retrait.resume")}
      contexte={contexte("retrait", "confirmer")}
    />
  ),
  // `T-2b`: the cash conclusion is rendered by `App` (the session's own read); this entry is the registry's completeness.
  comptant: () => (
    <EcranAVenir
      titre={t("client.comptant.titre")}
      resume={t("client.comptant.aucun")}
      contexte={contexte("comptant", "declarer")}
    />
  ),
  profil: () => (
    <EcranAVenir
      titre={t("client.moncompte.titre")}
      resume={t("client.moncompte.resume")}
      contexte={contexte("profil", "modifier")}
    />
  ),
  // `REG-PROFILE-001`, Arc B: the editor is rendered by `App` (the session's own read); this entry is the registry's completeness.
  declaration: () => (
    <EcranAVenir
      titre={t("client.declaration.titre")}
      resume={t("client.moncompte.resume")}
      contexte={contexte("declaration", "modifier")}
    />
  ),
  /**
   * The intake door, and round 209 is the round that opens it.
   *
   * It is PUBLIC , `resolveRoute` lets it past the whitelist because *"the
   * token IS the authorisation"* , so it renders with no session and no tab
   * bar. Standing it up now, inert and marked, is what lets round 209 replace
   * one component without touching the shell, the guard or the tab bar.
   */
  /**
   * `round C7`, `C7.3`: the REAL page is `deposit.tsx`, rendered by `App`
   * before the session guard (the holder has no session). This entry stays
   * so the registry remains total; the shell never reaches it for `/depot`.
   */
  depot: () => (
    <EcranAVenir
      titre={t("client.depot.titre")}
      resume={t("client.depot.resume")}
      contexte={contexte("depot", "photographier")}
    />
  ),
};
